import React, { useEffect, useState } from 'react';
import { ReactComponent as OccasiaLogoIcon } from '../../icons/occasia-logo-icon.svg';
import { useAuth } from '../../../contexts/AuthContext';
import messageThreadsApiService from '../../../services/messageThreadsApi';
import { getBackendUserId } from '../../../utils/messageThreadDisplay';
import {
  buildReadMapFromThreads,
  countUnreadThreads,
  getThreadReadMap,
  hasThreadReadMap,
  setThreadReadMap,
  threadListHasServerUnread,
} from '../../../utils/messageReadTracker';

const DashboardSidebar = ({ activeSection, onSectionChange, venueId }) => {
  const { currentUser, userProfile } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const actorUserId = getBackendUserId(currentUser, userProfile);
  const isAdmin = Boolean(userProfile?.is_admin || userProfile?.role === 'admin');

  useEffect(() => {
    let cancelled = false;
    if (!actorUserId) {
      setUnreadCount(0);
      return () => {};
    }
    const loadUnread = async () => {
      try {
        const threads = await messageThreadsApiService.listVenueDashboardThreads({
          userId: actorUserId,
          ownerId: actorUserId,
          venueId,
          limit: 50,
          isAdmin,
        });
        const venueThreads = messageThreadsApiService.filterThreadsForVenue(threads, venueId);
        if (cancelled) return;
        let readMap = getThreadReadMap(actorUserId);
        if (!hasThreadReadMap(actorUserId) && !threadListHasServerUnread(venueThreads)) {
          readMap = buildReadMapFromThreads(venueThreads);
          setThreadReadMap(actorUserId, readMap);
        }
        const count = countUnreadThreads(venueThreads, readMap);
        setUnreadCount(count);
      } catch (err) {
        if (!cancelled) setUnreadCount(0);
      }
    };
    loadUnread();
    const onReadUpdate = () => loadUnread();
    window.addEventListener('message-read-updated', onReadUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener('message-read-updated', onReadUpdate);
    };
  }, [actorUserId, venueId, isAdmin]);

  return (
    <div className="sidebar">
      <div className="sidebar-logo">
        <OccasiaLogoIcon className="occasia-logo" aria-label="Occasia" />
      </div>
      <div className="sidebar-icons">
        <button
          className={`sidebar-icon ${activeSection === 'home' ? 'active' : ''}`}
          onClick={() => onSectionChange('home')}
          onMouseEnter={() => {
            import('../components/BookingsTimeline');
            import('../components/ManageBookingsView');
          }}
          onFocus={() => {
            import('../components/BookingsTimeline');
            import('../components/ManageBookingsView');
          }}
          title="Home"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 22V12H15V22M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button
          className={`sidebar-icon ${activeSection === 'settings' ? 'active' : ''}`}
          onClick={() => onSectionChange('settings')}
          title="Settings"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23H11.92C11.3896 23 10.8809 22.7893 10.5058 22.4142C10.1307 22.0391 9.92001 21.5304 9.92001 21V20.91C9.91868 20.5882 9.82339 20.2738 9.64568 20.0055C9.46798 19.7372 9.21579 19.5268 8.92 19.4C8.61838 19.2669 8.28381 19.2272 7.95941 19.286C7.63502 19.3448 7.33568 19.4995 7.1 19.73L7.04 19.79C6.85425 19.976 6.63368 20.1235 6.39087 20.2241C6.14805 20.3248 5.88783 20.3766 5.625 20.3766C5.36217 20.3766 5.10195 20.3248 4.85913 20.2241C4.61632 20.1235 4.39575 19.976 4.21 19.79C4.02405 19.6043 3.87655 19.3837 3.77588 19.1409C3.67521 18.8981 3.62341 18.6378 3.62341 18.375C3.62341 18.1122 3.67521 17.8519 3.77588 17.6091C3.87655 17.3663 4.02405 17.1457 4.21 16.96L4.27 16.9C4.50054 16.6643 4.65522 16.365 4.714 16.0406C4.77278 15.7162 4.73308 15.3816 4.6 15.08C4.47324 14.7842 4.26279 14.532 3.99447 14.3543C3.72615 14.1766 3.41176 14.0813 3.09 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08V11.92C1 11.3896 1.21071 10.8809 1.58579 10.5058C1.96086 10.1307 2.46957 9.92001 3 9.92001H3.09C3.41176 9.91868 3.72615 9.82339 3.99447 9.64568C4.26279 9.46798 4.47324 9.21579 4.6 8.92C4.73308 8.61838 4.77278 8.28381 4.714 7.95941C4.65522 7.63502 4.50054 7.33568 4.27 7.1L4.21 7.04C4.02405 6.85425 3.87655 6.63368 3.77588 6.39087C3.67521 6.14805 3.62341 5.88783 3.62341 5.625C3.62341 5.36217 3.67521 5.10195 3.77588 4.85913C3.87655 4.61632 4.02405 4.39575 4.21 4.21C4.39575 4.02405 4.61632 3.87655 4.85913 3.77588C5.10195 3.67521 5.36217 3.62341 5.625 3.62341C5.88783 3.62341 6.14805 3.67521 6.39087 3.77588C6.63368 3.87655 6.85425 4.02405 7.04 4.21L7.1 4.27C7.33568 4.50054 7.63502 4.65522 7.95941 4.714C8.28381 4.77278 8.61838 4.73308 8.92 4.6H9C9.29579 4.47324 9.54798 4.26279 9.72568 3.99447C9.90339 3.72615 9.99868 3.41176 10 3.09V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1H12.08C12.6104 1 13.1191 1.21071 13.4942 1.58579C13.8693 1.96086 14.08 2.46957 14.08 3V3.09C14.0813 3.41176 14.1766 3.72615 14.3543 3.99447C14.532 4.26279 14.7842 4.47324 15.08 4.6C15.3816 4.73308 15.7162 4.77278 16.0406 4.714C16.365 4.65522 16.6643 4.50054 16.9 4.27L16.96 4.21C17.1457 4.02405 17.3663 3.87655 17.6091 3.77588C17.8519 3.67521 18.1122 3.62341 18.375 3.62341C18.6378 3.62341 18.8981 3.67521 19.1409 3.77588C19.3837 3.87655 19.6043 4.02405 19.79 4.21C19.976 4.39575 20.1235 4.61632 20.2241 4.85913C20.3248 5.10195 20.3766 5.36217 20.3766 5.625C20.3766 5.88783 20.3248 6.14805 20.2241 6.39087C20.1235 6.63368 19.976 6.85425 19.79 7.04L19.73 7.1C19.4995 7.33568 19.3448 7.63502 19.286 7.95941C19.2272 8.28381 19.2669 8.61838 19.4 8.92V9C19.5268 9.29579 19.7372 9.54798 20.0055 9.72568C20.2738 9.90339 20.5882 9.99868 20.91 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12V12.08C23 12.6104 22.7893 13.1191 22.4142 13.4942C22.0391 13.8693 21.5304 14.08 21 14.08H20.91C20.5882 14.0813 20.2738 14.1766 20.0055 14.3543C19.7372 14.532 19.5268 14.7842 19.4 15.08L19.32 15.08Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button
          className={`sidebar-icon ${activeSection === 'feedback' ? 'active' : ''}`}
          onClick={() => onSectionChange('feedback')}
          title="Feedback"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 2L14.09 8.26L21 9.27L16 14.14L17.18 21.02L12 17.77L6.82 21.02L8 14.14L3 9.27L9.91 8.26L12 2Z"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button
          className={`sidebar-icon ${activeSection === 'comms' ? 'active' : ''}`}
          onClick={() => onSectionChange('comms')}
          title="Messages"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M7 6.75H17C18.7949 6.75 20.25 8.20507 20.25 10V15C20.25 16.7949 18.7949 18.25 17 18.25H12.73C12.3316 18.25 11.9495 18.4082 11.6679 18.6898L9.53 20.8277C8.96068 21.397 8 20.9938 8 20.1886V18.25H7C5.20507 18.25 3.75 16.7949 3.75 15V10C3.75 8.20508 5.20508 6.75 7 6.75Z"
              fill="currentColor"
            />
          </svg>
          {unreadCount > 0 ? <span className="sidebar-message-badge">{unreadCount > 99 ? '99+' : unreadCount}</span> : null}
        </button>
      </div>
    </div>
  );
};

export default DashboardSidebar;

